/**
 * Token Tracker — Captures and accumulates OpenAI token usage per message.
 * Provides cost estimation based on GPT-4o-mini pricing.
 *
 * Pricing (as of March 2026):
 *   GPT-4o-mini: $0.15/1M input, $0.60/1M output
 *   GPT-4o:      $2.50/1M input, $10.00/1M output
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RoundUsage {
  prompt_tokens: number
  completion_tokens: number
  tool_calls: number
  tools_executed: string[]
}
export interface MessageTokenUsage {
  rounds: RoundUsage[]
  total_prompt_tokens: number
  total_completion_tokens: number
  total_tokens: number
  total_rounds: number
  total_tool_calls: number
  latency_ms: number
  services_used: string[]
  classifier_active: boolean
  estimated_cost_usd: number
}
export interface SessionUsage {
  total_prompt_tokens: number
  total_completion_tokens: number
  total_tokens: number
  total_messages: number
  total_tool_calls: number
  estimated_cost_usd: number
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PRICING: Record<
  string,
  {
    input: number
    output: number
  }
> = {
  'gpt-4o-mini': {
    input: 0.15,
    output: 0.6,
  },
  'gpt-4o': {
    input: 2.5,
    output: 10.0,
  },
  'gpt-4-turbo': {
    input: 10.0,
    output: 30.0,
  },
}
function estimateCost(
  promptTokens: number,
  completionTokens: number,
  model: string = 'gpt-4o-mini',
): number {
  const price = PRICING[model] || PRICING['gpt-4o-mini']
  return (
    (promptTokens * price.input + completionTokens * price.output) / 1_000_000
  )
}

// ─── Message Token Tracker ────────────────────────────────────────────────────

/**
 * Tracks token usage across multiple rounds of an agentic loop for a single message.
 * Create one per message, call addRound() for each OpenAI response, then getUsage().
 */
export class MessageTokenTracker {
  private rounds: RoundUsage[] = []
  private startTime: number
  private servicesUsed: string[] = []
  private classifierActive: boolean = false
  private model: string
  constructor(model: string = 'gpt-4o-mini') {
    this.startTime = Date.now()
    this.model = model
  }

  /** Set classifier metadata */
  setClassifierInfo(services: string[], active: boolean): void {
    this.servicesUsed = services
    this.classifierActive = active
  }

  /** Add a round from an OpenAI response */
  addRound(
    usage:
      | {
          prompt_tokens?: number
          completion_tokens?: number
          total_tokens?: number
        }
      | undefined,
    toolCalls: string[] = [],
  ): void {
    this.rounds.push({
      prompt_tokens: usage?.prompt_tokens || 0,
      completion_tokens: usage?.completion_tokens || 0,
      tool_calls: toolCalls.length,
      tools_executed: toolCalls,
    })
  }

  /** Get the complete usage summary */
  getUsage(): MessageTokenUsage {
    const totalPrompt = this.rounds.reduce((sum, r) => sum + r.prompt_tokens, 0)
    const totalCompletion = this.rounds.reduce(
      (sum, r) => sum + r.completion_tokens,
      0,
    )
    const totalToolCalls = this.rounds.reduce((sum, r) => sum + r.tool_calls, 0)
    return {
      rounds: this.rounds,
      total_prompt_tokens: totalPrompt,
      total_completion_tokens: totalCompletion,
      total_tokens: totalPrompt + totalCompletion,
      total_rounds: this.rounds.length,
      total_tool_calls: totalToolCalls,
      latency_ms: Date.now() - this.startTime,
      services_used: this.servicesUsed,
      classifier_active: this.classifierActive,
      estimated_cost_usd: estimateCost(
        totalPrompt,
        totalCompletion,
        this.model,
      ),
    }
  }
}

// ─── Session Accumulator ──────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'vsm_session_tokens'

/** Get accumulated session usage */
export function getSessionUsage(): SessionUsage {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch {}
  return {
    total_prompt_tokens: 0,
    total_completion_tokens: 0,
    total_tokens: 0,
    total_messages: 0,
    total_tool_calls: 0,
    estimated_cost_usd: 0,
  }
}

/** Add a message's usage to the session accumulator */
export function addToSessionUsage(messageUsage: MessageTokenUsage): void {
  const session = getSessionUsage()
  session.total_prompt_tokens += messageUsage.total_prompt_tokens
  session.total_completion_tokens += messageUsage.total_completion_tokens
  session.total_tokens += messageUsage.total_tokens
  session.total_messages += 1
  session.total_tool_calls += messageUsage.total_tool_calls
  session.estimated_cost_usd += messageUsage.estimated_cost_usd
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {}
}

/** Reset session usage */
export function resetSessionUsage(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {}
}

// ─── Formatting Helpers ───────────────────────────────────────────────────────

/** Format token count for display: 1234 → "1.2K", 123 → "123" */
export function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return String(count)
}

/** Format cost for display: 0.0015 → "$0.0015", 1.23 → "$1.23" */
export function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`
  if (usd < 1) return `$${usd.toFixed(3)}`
  return `$${usd.toFixed(2)}`
}

/** Format latency: 2345 → "2.3s", 450 → "450ms" */
export function formatLatency(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.round(ms)}ms`
}
